/**
 * Settings Page
 * Module 07 - Dashboard UI
 */

import React from 'react';
import { User, Database, Bell } from 'lucide-react';
import { useAuth } from '../hooks';
import { formatBytes, formatDate } from '../utils';

const Settings: React.FC = () => {
  const { user } = useAuth();

  const sections = [
    {
      title: 'Account Information',
      icon: User,
      items: [
        { label: 'Email', value: user?.email || 'Not available' },
        { label: 'Role', value: user?.role || 'user' },
        { label: 'Member Since', value: user?.createdAt ? formatDate(user.createdAt) : 'Unknown' },
      ],
    },
    {
      title: 'Storage & Quotas',
      icon: Database,
      items: [
        {
          label: 'Storage Used',
          value: user ? `${formatBytes(user.storageUsed)} / ${formatBytes(user.storageQuota)}` : '0 B / 0 B',
        },
        {
          label: 'Embeds',
          value: user ? `${user.embedsUsed} / ${user.embedQuota}` : '0 / 0',
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {sections.map((section) => (
          <div key={section.title} className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-lg bg-primary-100 p-2">
                <section.icon className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
            </div>

            <div className="space-y-3">
              {section.items.map((item) => (
                <div key={item.label} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm font-medium text-gray-600">{item.label}</span>
                  <span className="text-sm text-gray-900">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Coming Soon Sections */}
      <div className="card bg-gray-50">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-lg bg-gray-200 p-2">
            <Bell className="h-5 w-5 text-gray-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-600">Additional Settings</h2>
        </div>
        <p className="text-sm text-gray-500">
          More settings coming soon: Security preferences, notification settings, API keys, and more.
        </p>
      </div>
    </div>
  );
};

export default Settings;
